import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ADDONS } from '@fsp/core';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, ApiError, type AdminCompany } from '../api';
import { ADMIN_LOCALES, errorText, useI18n } from '../i18n';
import { Icon, type IconName } from '../icons';
import { Alert, AuthCard, LanguageSwitch, PageHead, ThemeToggle } from '../layout';
import { Messages, type Thread, type Ticket } from './support';

type View = AdminCompany['status'] | 'all' | 'email' | 'connected' | 'support' | 'messages' | 'shop' | 'contracts' | 'ai' | 'coupons';
const VIEWS: [View, string, IconName][] = [['pending', 'Pending', 'inbox'], ['active', 'Approved', 'check'], ['suspended', 'Suspended', 'archive'], ['rejected', 'Rejected', 'file'], ['closed', 'Closed accounts', 'archive'], ['all', 'All companies', 'users'], ['support', 'Support', 'help'], ['messages', 'Website messages', 'inbox'], ['shop', 'Shop', 'grid'], ['coupons', 'Discount codes', 'percent'], ['contracts', 'Agreements', 'file'], ['email', 'E-mail', 'note'], ['ai', 'AI assistant', 'spark'], ['connected', 'Connected panels', 'layers']];
const ACTIONS: Record<AdminCompany['status'], [AdminCompany['status'], string][]> = {
  pending: [['active', 'Approve'], ['rejected', 'Reject']],
  active: [['suspended', 'Suspend']],
  suspended: [['active', 'Reactivate']],
  rejected: [['active', 'Approve']],
  closed: [],
};
const STATUS_LABEL: Record<View, string> = { pending: 'Pending', active: 'Approved', rejected: 'Rejected', suspended: 'Suspended', closed: 'Closed', all: 'All', email: 'E-mail', connected: 'Connected panels', support: 'Support', messages: 'Website messages', shop: 'Shop', contracts: 'Agreements', ai: 'AI assistant', coupons: 'Discount codes' };
type AdminAction = { kind: 'ask' | 'view' | 'delete' | 'status' | 'password'; company: AdminCompany; status?: AdminCompany['status']; label?: string };

function AdminActionDialog({ action, busy, error, onClose, onConfirm }: {
  action: AdminAction | null; busy: boolean; error: Error | null; onClose: () => void; onConfirm: (value: string) => Promise<void>;
}) {
  const { t } = useI18n();
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => { if (action) dialog.current?.showModal(); else dialog.current?.close(); }, [action]);
  if (!action) return <dialog ref={dialog} className="confirm-dialog" />;
  const { company, kind } = action;
  const message = kind === 'ask' ? t('Why do you need to see the panel of “{company}”? The owner reads this.', { company: company.name })
    : kind === 'view' ? t('Open the panel of “{company}” as its owner (read-only, 1 hour)?', { company: company.name })
      : kind === 'delete' ? t('Delete “{company}” for good, with all its invoices and files? Download the archive first. Type the company name to confirm:', { company: company.name })
        : kind === 'password' ? t('Create a temporary password for {email}? The current password stops working.', { email: company.ownerEmail ?? '' })
          : t('{action} “{company}”?', { action: t(action.label ?? ''), company: company.name });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void onConfirm(String(new FormData(event.currentTarget).get('value') ?? '').trim());
  };
  return <dialog ref={dialog} className="confirm-dialog" onClose={onClose}>
    <form className="dialog-card" onSubmit={submit} key={`${kind}-${company.id}`}>
      <h2>{company.name}</h2><p>{message}</p>
      {error ? <Alert kind="error">{errorText(t, error)}</Alert> : null}
      {kind === 'ask' ? <label>{t('Reason')}<textarea name="value" minLength={5} maxLength={500} required autoFocus rows={3} /></label> : null}
      {kind === 'delete' ? <label>{t('Company name')}<input name="value" required autoFocus autoComplete="off" /></label> : null}
      <div className="dialog-actions">
        <button type="button" className="button button--ghost" onClick={onClose} disabled={busy}>{t('Cancel')}</button>
        <button type="submit" className={`button ${kind === 'delete' ? 'button--danger' : 'button--primary'}`} disabled={busy}>{t(kind === 'ask' ? 'Ask for access' : kind === 'view' ? 'View as' : kind === 'delete' ? 'Delete for good' : kind === 'password' ? 'Temporary password' : action.label ?? 'Save')}</button>
      </div>
    </form>
  </dialog>;
}

interface MailSettings { smtp_host: string; smtp_port: string; smtp_security: string; smtp_user: string; mail_from: string; mail_from_name: string; notify_email: string; hasPassword: boolean }

function EmailSettings() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['admin-mail'], queryFn: () => api.get<{ settings: MailSettings }>('api/admin/email').then((r) => r.settings) });
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const save = useMutation({
    mutationFn: (body: unknown) => api.put('api/admin/email', body),
    onSuccess: async () => { setMessage({ kind: 'success', text: t('E-mail settings saved.') }); await queryClient.invalidateQueries({ queryKey: ['admin-mail'] }); },
    onError: () => setMessage({ kind: 'error', text: t('Check the fields.') }),
  });
  const test = useMutation({
    mutationFn: () => api.post<{ to: string }>('api/admin/email/test'),
    onSuccess: (r) => setMessage({ kind: 'success', text: t('Test e-mail sent to {email}.', { email: r.to }) }),
    onError: () => setMessage({ kind: 'error', text: t('Test e-mail could not be sent. Check the settings and the error log.') }),
  });
  const s = settings.data;
  if (!s) return null;
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const d = Object.fromEntries(new FormData(event.currentTarget)) as Record<string, string>;
    save.mutate({ ...d, ...(d.smtp_password ? {} : { smtp_password: undefined }) });
  };
  const configured = Boolean(s.smtp_host && s.smtp_user && s.mail_from && s.hasPassword);
  const field = (key: keyof MailSettings, label: string, type = 'text') => <label>{t(label)}<input type={type} name={key} defaultValue={String(s[key] ?? '')} autoComplete="off" /></label>;
  return (
    <>
      {message ? <Alert kind={message.kind}>{message.text}</Alert> : null}
      <div className="two-cols two-cols--form">
        <section className="panel-card"><p className="eyebrow">SMTP</p><h2>{t('Sending account')}</h2>
          <form onSubmit={submit}>
            <div className="form-grid">
              {field('smtp_host', 'SMTP server')}{field('smtp_port', 'Port')}
              <label>{t('Encryption')}<select name="smtp_security" defaultValue={s.smtp_security || 'tls'}><option value="tls">STARTTLS</option><option value="ssl">SSL/TLS</option><option value="none">{t('None')}</option></select></label>
              {field('smtp_user', 'SMTP user')}
              <label className="span-2">{t('SMTP password')}<input type="password" name="smtp_password" autoComplete="new-password" placeholder={s.hasPassword ? t('Saved — leave empty to keep it') : ''} /></label>
              {field('mail_from', 'Sender address', 'email')}{field('mail_from_name', 'Sender name')}
            </div>
            {field('notify_email', 'Send new-registration notices to', 'email')}
            <button className="button button--primary" disabled={save.isPending}>{t('Save')}</button>
          </form>
        </section>
        <section className="panel-card"><p className="eyebrow">{t('Status')}</p><h2>{configured ? t('Configured') : t('Not configured')}</h2>
          <p className="muted">{t('Until SMTP is configured, registration and approval still work; only the e-mails are skipped.')}</p>
          <button type="button" className="button button--ghost" disabled={!configured || !s.notify_email || test.isPending} onClick={() => test.mutate()}>{t('Send test e-mail')}</button>
        </section>
      </div>
    </>
  );
}

interface ConnectedSite { id: number; companyId: number; companyName: string; keyId: string; origin: string; basePath: string; createdAt: string; revokedAt: string | null; lastSeenAt: string | null }

/**
 * Connected panels: the app under a customer's own address (e.g. https://arkabi.lt/fsp/). The admin
 * creates an installation and downloads its fsp.php, which the customer puts in public_html.
 */
function ConnectedSites() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const sites = useQuery({ queryKey: ['admin-sites'], queryFn: () => api.get<{ sites: ConnectedSite[] }>('api/admin/connected-sites').then((r) => r.sites) });
  const companies = useQuery({ queryKey: ['admin-companies', 'active'], queryFn: () => api.get<{ companies: AdminCompany[] }>('api/admin/companies?status=active') });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin-sites'] });
  const create = useMutation({ mutationFn: (body: unknown) => api.post('api/admin/connected-sites', body), onSuccess: refresh });
  const revoke = useMutation({ mutationFn: (id: number) => api.post(`api/admin/connected-sites/${id}/revoke`), onSuccess: refresh });
  const error = create.error ?? revoke.error;
  const code = error instanceof ApiError ? error.code : '';
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const d = new FormData(event.currentTarget);
    create.mutate({ companyId: Number(d.get('companyId')), origin: String(d.get('origin')), basePath: String(d.get('basePath')) });
  };
  return (
    <>
      {error ? <Alert kind="error">{code === 'invalid_origin' ? t('Enter the website address as https://example.lt') : code === 'invalid_base_path' ? t('The folder may contain only letters, digits, - and _.') : errorText(t, error)}</Alert> : null}
      <div className="two-cols two-cols--form">
        <section className="panel-card"><div className="table-wrap"><table className="invoice-table admin-table">
          <thead><tr><th>{t('Address')}</th><th>{t('Company')}</th><th>{t('Last used')}</th><th /></tr></thead>
          <tbody>
            {sites.isSuccess && !sites.data.length ? <tr><td colSpan={4} className="empty">{t('No connected panels yet.')}</td></tr> : null}
            {sites.data?.map((s) => (
              <tr key={s.id} className={s.revokedAt ? 'is-revoked' : undefined}>
                <td><strong>{s.origin}{s.basePath}/</strong><small>{t('Key')} {s.keyId}</small></td>
                <td>{s.companyName}</td>
                <td>{s.revokedAt ? <span className="badge badge--suspended">{t('Revoked')}</span> : s.lastSeenAt ? new Date(s.lastSeenAt).toLocaleString() : t('Not installed yet')}</td>
                <td className="row-actions">{s.revokedAt ? null : <>
                  <a className="button button--small button--primary" href={`api/admin/connected-sites/${s.id}/file`} download="fsp.php">{t('Download fsp.php')}</a>
                  <button type="button" className="button button--small button--ghost" onClick={() => { if (confirm(t('Revoke {address}? The panel on that website stops working at once.', { address: s.origin + s.basePath }))) revoke.mutate(s.id); }}>{t('Revoke')}</button>
                </>}</td>
              </tr>
            ))}
          </tbody>
        </table></div></section>
        <section className="panel-card"><p className="eyebrow">{t('New installation')}</p><h2>{t('Connect a website')}</h2>
          <form onSubmit={submit}>
            <label>{t('Company')}<select name="companyId" required>{companies.data?.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
            <label>{t('Website')}<input name="origin" placeholder="https://arkabi.lt" required /></label>
            <label>{t('Folder')}<input name="basePath" defaultValue="/fsp" /></label>
            <button className="button button--primary" disabled={create.isPending}>{t('Create')}</button>
          </form>
          <p className="muted">{t('Send the downloaded fsp.php to the customer: they put it in public_html and open https://their-site/fsp.php once. It installs itself in the folder. Uploading a newer fsp.php the same way updates it.')}</p>
        </section>
      </div>
    </>
  );
}

export function AdminLoginPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const login = useMutation({
    mutationFn: (v: { username: string; password: string }) => api.post('api/admin/login', v),
    onSuccess: () => navigate({ to: '/admin' }),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    login.mutate({ username: String(data.get('username')), password: String(data.get('password')) });
  };
  return (
    <AuthCard eyebrow={t('Platform administration')} title={t('Sign in')} intro={t('Administrator access only.')}>
      {login.isError ? <Alert kind="error">{t(login.error instanceof ApiError && login.error.code === 'too_many_attempts' ? 'Too many attempts. Try again later.' : 'Sign-in failed.')}</Alert> : null}
      <form onSubmit={submit} autoComplete="on">
        <label>{t('Username')}<input name="username" autoComplete="username" required autoFocus /></label>
        <label>{t('Password')}<input type="password" name="password" autoComplete="current-password" required /></label>
        <button className="button button--primary" type="submit" disabled={login.isPending}>{t('Sign in')}</button>
      </form>
    </AuthCard>
  );
}

export function AdminPage({ view }: { view: View }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [action, setAction] = useState<AdminAction | null>(null);
  const companies = useQuery({
    enabled: !['email', 'connected', 'support', 'messages', 'shop', 'contracts', 'ai', 'coupons'].includes(view),
    queryKey: ['admin-companies', view],
    queryFn: () => api.get<{ companies: AdminCompany[] }>(`api/admin/companies?status=${view}`),
    retry: false,
  });
  const tempPassword = useMutation({
    mutationFn: (id: number) => api.post<{ email: string; password: string }>(`api/admin/companies/${id}/temporary-password`),
    onSuccess: (r) => setNotice(t('Temporary password for {email}: {password} — it must be changed at the first sign-in.', { email: r.email, password: r.password })),
  });
  const viewAs = useMutation({
    mutationFn: (id: number) => api.post(`api/admin/companies/${id}/view-as`),
    onSuccess: () => { window.location.assign('./'); },
  });
  // B3a: access needs the owner's approval; the request is e-mailed to the owner.
  const askAccess = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => api.post<{ mailSent: boolean }>(`api/admin/companies/${id}/view-requests`, { reason }),
    onSuccess: async (r) => {
      setNotice(r.mailSent ? t('Access request e-mailed to the owner. You can open the panel once they approve.') : t('Request saved, but the e-mail could not be sent. Check the e-mail settings.'));
      await queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
    },
  });
  const [planOf, setPlanOf] = useState<AdminCompany | null>(null);
  const removeForGood = useMutation({
    mutationFn: ({ id, confirmName }: { id: number; confirmName: string }) => api.delete(`api/admin/companies/${id}`, { confirmName }),
    onSuccess: async () => { setNotice(t('Company deleted.')); await queryClient.invalidateQueries({ queryKey: ['admin-companies'] }); },
  });
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) => api.post(`api/admin/companies/${id}/status`, { status }),
    onSuccess: async (_data, variables) => {
      setNotice(t('Company status updated: {status}.', { status: t(STATUS_LABEL[variables.status as View]) }));
      await queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
    },
  });
  const confirmAction = async (value: string) => {
    if (!action) return;
    try {
      if (action.kind === 'ask') await askAccess.mutateAsync({ id: action.company.id, reason: value });
      else if (action.kind === 'view') await viewAs.mutateAsync(action.company.id);
      else if (action.kind === 'delete') await removeForGood.mutateAsync({ id: action.company.id, confirmName: value });
      else if (action.kind === 'password') await tempPassword.mutateAsync(action.company.id);
      else if (action.status) await setStatus.mutateAsync({ id: action.company.id, status: action.status });
      setAction(null);
    } catch { /* The mutation error remains visible in the dialog. */ }
  };
  if (companies.error instanceof ApiError && companies.error.status === 401) {
    void navigate({ to: '/admin/login' });
    return null;
  }
  const logout = async () => {
    await api.post('api/admin/logout').catch(() => undefined);
    await navigate({ to: '/admin/login' });
  };
  const rows = companies.data?.companies ?? [];
  return (
    <div className="app-shell">
      <aside className={menuOpen ? 'sidebar is-open' : 'sidebar'}>
        <div className="sidebar-head"><a className="panel-brand" href="/admin"><img src="media/favicon.png" alt="" width={28} height={28} /><span>FSP</span><em>admin</em></a></div>
        <nav>{VIEWS.map(([key, label, icon]) => (
          <a key={key} href={`/admin?view=${key}`} className={key === view ? 'active' : undefined} onClick={(e) => { e.preventDefault(); setMenuOpen(false); void navigate({ to: '/admin', search: { view: key } }); }}>
            <span className="nav-icon"><Icon name={icon} /></span><span className="nav-label">{t(label)}</span>
          </a>))}</nav>
        <div className="logout"><button type="button" onClick={logout}><span className="nav-icon"><Icon name="logout" /></span><span className="nav-label">{t('Sign out')}</span></button></div>
      </aside>
      <button type="button" className="sidebar-scrim" aria-label={t('Close menu')} onClick={() => setMenuOpen(false)} />
      <main className="content">
        <header className="topbar">
          <button className="menu-toggle" type="button" aria-label={t('Menu')} aria-expanded={menuOpen} onClick={() => setMenuOpen(!menuOpen)}>☰</button>
          <PageHead eyebrow={view === 'email' || view === 'connected' || view === 'ai' ? t('Integrations') : view === 'support' || view === 'messages' || view === 'shop' || view === 'contracts' || view === 'coupons' ? t('Customers') : t('Companies')} title={t(STATUS_LABEL[view])} text={view === 'coupons' ? t('Discount codes for the plans (for example for the first accounting firms). The customer enters the code on the Stripe payment page.') : view === 'ai' ? t('The key of the Claude API (Anthropic) and the prices of the AI assistant add-on. The key is kept encrypted and never shown again.') : view === 'contracts' ? t('The service agreement with Arkabi: write and approve the text, then check the agreements customers upload signed (GoSign).') : view === 'shop' ? t('What customers switched off and which coming add-ons they want. Plans and add-ons are listed in the code (@fsp/core shop.ts).') : view === 'messages' ? t('Messages from the contact form on fakturasmartpro.com. Reply by e-mail, then mark as handled.') : view === 'support' ? t('Requests from companies. Your answer is e-mailed to the person who asked.') : view === 'connected' ? t('The panel under a customer\'s own website address, through one proxy file that keeps no data.') : view === 'email' ? t('SMTP account for platform notices: new registrations to you, approval confirmations to companies. Invoices keep using each project\'s own SMTP.') : view === 'pending' ? t('New registrations wait here. A company can sign in only after you approve it.') : t('Change a company\'s access at any time. Suspended companies are signed out immediately.')} />
          <div className="workspace-status"><span className="status-dot" />{t('Platform administration')}</div>
          <LanguageSwitch locales={ADMIN_LOCALES} />
          <ThemeToggle />
        </header>
        {view === 'email' ? <EmailSettings /> : null}
        {view === 'connected' ? <ConnectedSites /> : null}
        {view === 'support' ? <AdminSupport /> : null}
        {view === 'messages' ? <WebsiteMessages /> : null}
        {view === 'shop' ? <AdminShop /> : null}
        {view === 'contracts' ? <AdminContracts /> : null}
        {view === 'coupons' ? <AdminCoupons /> : null}
        {view === 'ai' ? <AiSettings /> : null}
        {notice && !['email', 'connected', 'support', 'messages', 'shop', 'contracts', 'ai', 'coupons'].includes(view) ? <Alert kind="success">{notice}</Alert> : null}
        {setStatus.isError || viewAs.isError || removeForGood.isError || askAccess.isError ? <Alert kind="error">{errorText(t, setStatus.error ?? viewAs.error ?? removeForGood.error ?? askAccess.error)}</Alert> : null}
        {['email', 'connected', 'support', 'messages', 'shop', 'contracts', 'ai', 'coupons'].includes(view) ? null : <section className="panel-card"><div className="table-wrap"><table className="invoice-table admin-table">
          <thead><tr><th>{t('Company')}</th><th>{t('Owner')}</th><th>{t('Plan')}</th><th>{t('Registered')}</th><th>{t('Status')}</th><th /></tr></thead>
          <tbody>
            {companies.isSuccess && !rows.length ? <tr><td colSpan={6} className="empty">{t('No companies here.')}</td></tr> : null}
            {rows.map((c) => (
              <tr key={c.id}>
                <td><strong>{c.name}</strong><small>{c.code || '—'}</small></td>
                <td>{c.ownerName ?? '—'}<small>{c.ownerEmail ?? ''}</small></td>
                <td>{planLabel(t, c)}<small>{c.planNote ?? ''}</small></td>
                <td>{new Date(c.createdAt).toLocaleString()}<small>{c.lastLoginAt ? `${t('Last sign-in')}: ${new Date(c.lastLoginAt).toLocaleString()}` : t('Never signed in')}</small></td>
                <td><span className={`badge badge--${c.status}`}>{t(STATUS_LABEL[c.status])}</span></td>
                <td className="row-actions">{ACTIONS[c.status].map(([status, label]) => (
                  <button key={status} type="button" className={`button button--small ${status === 'active' ? 'button--primary' : 'button--ghost'}`}
                    onClick={() => { if (status === 'active') setStatus.mutate({ id: c.id, status }); else setAction({ kind: 'status', company: c, status, label }); }}>{t(label)}</button>
                ))}{c.status !== 'closed' ? <button type="button" className="button button--small button--ghost" onClick={() => setPlanOf(c)}>{t('Plan')}</button> : null}
                <a className="button button--small button--ghost" href={`api/admin/companies/${c.id}/archive`} download title={t('All invoices (PDF), files and lists as a ZIP.')}>{t('Archive')}</a>
                {c.status === 'closed' ? (
                  <button type="button" className="button button--small button--danger" onClick={() => setAction({ kind: 'delete', company: c })}>{t('Delete for good')}</button>
                ) : null}{c.status === 'active' && c.ownerEmail ? (
                  c.viewRequest?.state === 'approved' ? (
                    <button type="button" className="button button--small button--primary" title={t('Open this company\'s panel read-only for one hour. Recorded in the audit log.')}
                      onClick={() => setAction({ kind: 'view', company: c })}>{t('View as')}</button>
                  ) : c.viewRequest?.state === 'pending' ? (
                    <span className="badge badge--pending" title={new Date(c.viewRequest.requestedAt).toLocaleString()}>{t('Waiting for the owner')}</span>
                  ) : (
                    <button type="button" className="button button--small button--ghost" title={t('The owner gets an e-mail and decides. An approval is good for one read-only hour within 24 hours.')}
                      onClick={() => setAction({ kind: 'ask', company: c })}>
                      {c.viewRequest?.state === 'declined' ? t('Declined — ask again') : t('Ask for access')}</button>
                  )
                ) : null}{c.status === 'active' && c.ownerEmail ? (
                  <button type="button" className="button button--small button--ghost" onClick={() => setAction({ kind: 'password', company: c })}>{t('Temporary password')}</button>
                ) : null}</td>
              </tr>
            ))}
          </tbody>
        </table></div></section>}
        <PlanDialog company={planOf} onClose={() => setPlanOf(null)} onSaved={async () => { setPlanOf(null); await queryClient.invalidateQueries({ queryKey: ['admin-companies'] }); }} />
        <AdminActionDialog action={action} busy={setStatus.isPending || viewAs.isPending || removeForGood.isPending || askAccess.isPending || tempPassword.isPending}
          error={(setStatus.error ?? viewAs.error ?? removeForGood.error ?? askAccess.error ?? tempPassword.error) as Error | null}
          onClose={() => setAction(null)} onConfirm={confirmAction} />
      </main>
    </div>
  );
}

const SUPPORT_STATUS: Record<Ticket['status'], string> = { open: 'Waiting', answered: 'Answered', closed: 'Closed' };

/** Admin: requests of all companies; answer, close. */
function AdminSupport() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'open' | 'answered' | 'closed' | 'all'>('open');
  const [openId, setOpenId] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const list = useQuery({ queryKey: ['admin-support', status], queryFn: () => api.get<{ tickets: Ticket[] }>(`api/admin/support?status=${status}`).then((r) => r.tickets) });
  const thread = useQuery({ queryKey: ['admin-support', 'thread', openId], enabled: openId !== null, queryFn: () => api.get<Thread>(`api/admin/support/${openId}`) });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin-support'] });
  const answer = useMutation({ mutationFn: (message: string) => api.post(`api/admin/support/${openId}/messages`, { message }), onSuccess: async () => { setDraft(''); await refresh(); } });
  const close = useMutation({ mutationFn: () => api.post(`api/admin/support/${openId}/close`), onSuccess: refresh });
  const error = answer.error ?? close.error;
  return (
    <>
      {error ? <Alert kind="error">{errorText(t, error)}</Alert> : null}
      <div className="support-layout">
        <aside className="panel-card">
          <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} aria-label={t('Status')}>
            {(['open', 'answered', 'closed', 'all'] as const).map((s) => <option key={s} value={s}>{t(s === 'all' ? 'All' : SUPPORT_STATUS[s])}</option>)}
          </select>
          <div className="support-list">
            {list.isSuccess && !list.data.length ? <p className="muted">{t('No requests here.')}</p> : null}
            {list.data?.map((x) => (
              <button key={x.id} type="button" className={`support-item ${x.id === openId ? 'active' : ''}`} onClick={() => setOpenId(x.id)}>
                <strong>{x.subject}</strong><small>{x.companyName} · {new Date(x.updatedAt ?? x.createdAt).toLocaleString()}</small>
              </button>
            ))}
          </div>
        </aside>
        <section className="panel-card">
          {thread.data ? (
            <>
              <div className="card-head"><div><p className="eyebrow">#{thread.data.ticket.id} · {thread.data.ticket.companyName} · {t(SUPPORT_STATUS[thread.data.ticket.status])}</p><h2>{thread.data.ticket.subject}</h2>
                <small className="muted">{thread.data.ticket.userName} {thread.data.ticket.userEmail ? `<${thread.data.ticket.userEmail}>` : ''}</small></div>
                {thread.data.ticket.status !== 'closed' ? <button type="button" className="button button--ghost button--small" onClick={() => close.mutate()}>{t('Close')}</button> : null}</div>
              <Messages messages={thread.data.messages} mine="admin" />
              <form onSubmit={(e: FormEvent) => { e.preventDefault(); if (draft.trim()) answer.mutate(draft.trim()); }}>
                <label>{t('Answer (sent by e-mail too)')}<textarea rows={4} value={draft} onChange={(e) => setDraft(e.target.value)} required /></label>
                <div className="form-actions"><button className="button button--primary" disabled={answer.isPending}>{t('Send')}</button></div>
              </form>
            </>
          ) : <p className="muted">{t('Choose a request.')}</p>}
        </section>
      </div>
    </>
  );
}

interface WebsiteMessage { id: number; name: string; email: string; company: string | null; phone: string | null; message: string; locale: string | null; createdAt: string; handledAt: string | null }

/** Contact form messages from the public website. */
/** Parduotuvė: demand for coming add-ons and sections switched off, over active companies. */
function AdminShop() {
  const { t } = useI18n();
  const stats = useQuery({ queryKey: ['admin-addons'], queryFn: () => api.get<{ addons: { addon: string; interested: number; off: number; on: number }[] }>('api/admin/addons').then((r) => new Map(r.addons.map((a) => [a.addon, a]))) });
  return (
    <section className="panel-card"><div className="table-wrap"><table className="invoice-table admin-table">
      <thead><tr><th>{t('Add-on')}</th><th>{t('Status')}</th><th>{t('Price')}</th><th>{t('Waiting for it')}</th><th>{t('Switched off')}</th></tr></thead>
      <tbody>{ADDONS.map((a) => {
        const s = stats.data?.get(a.id);
        return (
          <tr key={a.id}>
            <td><strong>{a.name}</strong><br /><small className="muted">{a.id}</small></td>
            <td>{a.status === 'soon' ? t('Coming') : t('Available')}</td>
            <td>{a.priceCents === null ? '—' : a.priceCents === 0 ? t('Free') : `${(a.priceCents / 100).toFixed(2)} €`}</td>
            <td>{a.status === 'soon' ? s?.interested ?? 0 : '—'}</td>
            <td>{a.module ? s?.off ?? 0 : '—'}</td>
          </tr>
        );
      })}</tbody>
    </table></div></section>
  );
}

interface AiView {
  settings: { model: string; workspaceId: string; inputCents: number; outputCents: number; markup: number; priceAi: string; priceAiTopup: string; keySet: boolean; keyLast4: string };
  fromEnvironment: { key: boolean; priceAi: boolean; priceAiTopup: boolean }; stripe: boolean; active: boolean;
}

/** Integrations → AI: the Anthropic key (write-only), model, our prices and margin, the add-on's Stripe prices. */
function AiSettings() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const data = useQuery({ queryKey: ['admin-ai'], queryFn: () => api.get<AiView>('api/admin/ai') });
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: (body: object) => api.put<AiView>('api/admin/ai', body),
    onSuccess: (r) => { queryClient.setQueryData(['admin-ai'], r); setSaved(true); },
  });
  const d = data.data;
  if (!d) return null;
  const s = d.settings;
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    setSaved(false);
    save.mutate({ model: String(f.get('model')), workspaceId: String(f.get('workspaceId') ?? '').trim(), inputCents: Number(f.get('inputCents')), outputCents: Number(f.get('outputCents')), markup: Number(f.get('markup')),
      priceAi: String(f.get('priceAi') ?? s.priceAi).trim(), priceAiTopup: String(f.get('priceAiTopup') ?? s.priceAiTopup).trim(), apiKey: String(f.get('apiKey') ?? '').trim() || undefined });
    (form.elements.namedItem('apiKey') as HTMLInputElement).value = '';
  };
  const removeKey = () => {
    if (!confirm(t('Remove the key? The AI assistant stops for every company.'))) return;
    setSaved(false);
    save.mutate({ model: s.model, workspaceId: s.workspaceId, inputCents: s.inputCents, outputCents: s.outputCents, markup: s.markup, priceAi: s.priceAi, priceAiTopup: s.priceAiTopup, clearKey: true });
  };
  return (
    <section className="panel-card">
      <p className="eyebrow">{t('Status')}</p><h2>{d.active ? t('Active') : t('Not configured')}</h2>
      {saved ? <Alert kind="success">{t('Saved.')}</Alert> : null}
      {save.isError ? <Alert kind="error">{errorText(t, save.error)}</Alert> : null}
      <form className="ai-settings" onSubmit={submit}>
        <label>{t('Claude API key')}
          <input type="password" name="apiKey" autoComplete="new-password" data-1p-ignore data-lpignore="true" spellCheck={false} placeholder={s.keySet ? `•••• ${s.keyLast4}` : 'sk-ant-…'} disabled={d.fromEnvironment.key} />
        </label>
        <p className="muted">{d.fromEnvironment.key ? t('The key is set in the server environment; it is used instead of this field.')
          : t('Create a key at console.anthropic.com (API keys). It is checked with Anthropic before it is saved; leave the field empty to keep the current key.')}</p>
        <label>{t('Anthropic workspace ID (for keys without a workspace)')}
          <input name="workspaceId" defaultValue={s.workspaceId} placeholder="wrkspc_…" disabled={d.fromEnvironment.key} />
        </label>
        <p className="muted">{t('Find this ID in Claude Console → Settings → Workspaces. Leave it empty for a key tied to one workspace.')}</p>
        <div className="form-grid">
          <label>{t('Model')}<input name="model" defaultValue={s.model} required /></label>
          <label>{t('Margin (× our token cost)')}<input type="number" name="markup" min={1} max={20} step={0.1} defaultValue={s.markup} required /></label>
          <label>{t('Input price, euro cents per million tokens')}<input type="number" name="inputCents" min={1} step={1} defaultValue={s.inputCents} required /></label>
          <label>{t('Output price, euro cents per million tokens')}<input type="number" name="outputCents" min={1} step={1} defaultValue={s.outputCents} required /></label>
          <label>{t('Stripe price: AI add-on, 5 € a month')}<input name="priceAi" defaultValue={s.priceAi} placeholder="price_…" disabled={d.fromEnvironment.priceAi} /></label>
          <label>{t('Stripe price: AI credit top-up, 5 € once')}<input name="priceAiTopup" defaultValue={s.priceAiTopup} placeholder="price_…" disabled={d.fromEnvironment.priceAiTopup} /></label>
        </div>
        {!d.stripe ? <p className="muted">{t('Stripe is not set up on the server: the add-on can only be used by companies on a free plan.')}</p> : null}
        <div className="form-actions">
          <button className="button button--primary" disabled={save.isPending}>{t('Save')}</button>
          {s.keySet && !d.fromEnvironment.key ? <button type="button" className="button button--ghost" disabled={save.isPending} onClick={removeKey}>{t('Remove the key')}</button> : null}
        </div>
      </form>
    </section>
  );
}

interface ContractText { version: number; body: string; status: 'draft' | 'approved'; createdAt: string; approvedAt: string | null }
interface CompanyContract { companyId: number; company: string; free: boolean; id: number | null; number: string | null; status: string | null; signedName: string | null; uploadedAt: string | null; reviewNote: string | null }

/** The agreement's text (versions: draft → approved) and the agreements customers uploaded signed. */
function AdminContracts() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const texts = useQuery({ queryKey: ['admin-contract-texts'], queryFn: () => api.get<{ texts: ContractText[]; draft: string; fields: string[] }>('api/admin/contract-texts') });
  const list = useQuery({ queryKey: ['admin-contracts'], queryFn: () => api.get<{ contracts: CompanyContract[] }>('api/admin/contracts').then((r) => r.contracts) });
  const act = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onSuccess: () => Promise.all(['admin-contract-texts', 'admin-contracts'].map((key) => queryClient.invalidateQueries({ queryKey: [key] }))) });
  const [body, setBody] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<number, string>>({});
  const latest = texts.data?.texts[0];
  const text = body ?? latest?.body ?? texts.data?.draft ?? '';
  const STATUS: Record<string, string> = { issued: 'Downloaded', uploaded: 'Signed — to check', accepted: 'Accepted', returned: 'Returned' };
  return (
    <>
      {act.isError ? <Alert kind="error">{errorText(t, act.error)}</Alert> : null}
      <section className="panel-card">
        <div className="card-head"><div><p className="eyebrow">{t('Text')}</p><h2>{latest ? `v${latest.version} · ${latest.status === 'approved' ? t('Approved') : t('Draft')}` : t('No text yet — a draft is filled in below')}</h2></div></div>
        <p className="muted">{t('Fields filled for each company')}: {texts.data?.fields.map((f) => `{${f}}`).join(' ')}. {t('Lines starting with "# " are headings. Customers get the latest approved version only; check it with a lawyer before approving.')}</p>
        <textarea className="contract-text" rows={18} value={text} onChange={(e) => setBody(e.target.value)} />
        <div className="form-actions">
          <button type="button" className="button button--primary" disabled={act.isPending || !text.trim()} onClick={() => act.mutate(async () => { await api.post('api/admin/contract-texts', { body: text }); setBody(null); })}>{t('Save as a new version')}</button>
        </div>
        <ul className="contract-versions">
          {texts.data?.texts.map((v) => (
            <li key={v.version}><strong>v{v.version}</strong> · {v.status === 'approved' ? t('Approved') : t('Draft')} · {new Date(v.createdAt).toLocaleDateString()}
              {' '}<a href={`api/admin/contract-texts/${v.version}/preview.pdf`} target="_blank" rel="noreferrer">{t('Preview PDF')}</a>
              {v.status === 'draft' ? <button type="button" className="text-link" disabled={act.isPending} onClick={() => act.mutate(() => api.post(`api/admin/contract-texts/${v.version}/approve`))}>{t('Approve for customers')}</button> : null}</li>
          ))}
        </ul>
      </section>
      <section className="panel-card"><div className="table-wrap"><table className="invoice-table admin-table">
        <thead><tr><th>{t('Company')}</th><th>{t('Agreement')}</th><th>{t('Status')}</th><th>{t('File')}</th><th /></tr></thead>
        <tbody>{list.data?.map((c) => (
          <tr key={c.companyId}>
            <td><strong>{c.company}</strong>{c.free ? <small>{t('Free plan — not needed')}</small> : null}</td>
            <td>{c.number ?? '—'}</td>
            <td>{c.status ? t(STATUS[c.status] ?? c.status) : t('Not downloaded')}{c.reviewNote ? <small>{c.reviewNote}</small> : null}</td>
            <td>{c.id && c.signedName ? <a href={`api/admin/contracts/${c.id}/file`}>{c.signedName}</a> : '—'}{c.uploadedAt ? <small>{new Date(c.uploadedAt).toLocaleDateString()}</small> : null}</td>
            <td className="row-actions">{c.id && c.status === 'uploaded' ? <>
              <input className="contract-note" placeholder={t('Note if returned')} value={notes[c.id] ?? ''} onChange={(e) => setNotes({ ...notes, [c.id!]: e.target.value })} />
              <button type="button" className="button button--small button--primary" disabled={act.isPending} onClick={() => act.mutate(() => api.post(`api/admin/contracts/${c.id}/review`, { decision: 'accept' }))}>{t('Accept')}</button>
              <button type="button" className="button button--small button--ghost" disabled={act.isPending} onClick={() => act.mutate(() => api.post(`api/admin/contracts/${c.id}/review`, { decision: 'return', note: notes[c.id!] ?? '' }))}>{t('Return')}</button>
            </> : null}</td>
          </tr>
        ))}</tbody>
      </table></div></section>
    </>
  );
}

function WebsiteMessages() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [all, setAll] = useState(false);
  const list = useQuery({ queryKey: ['admin-messages', all], queryFn: () => api.get<{ messages: WebsiteMessage[] }>(`api/admin/messages?status=${all ? 'all' : 'open'}`).then((r) => r.messages) });
  const handled = useMutation({ mutationFn: (id: number) => api.post(`api/admin/messages/${id}/handled`), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-messages'] }) });
  return (
    <section className="panel-card">
      <div className="card-head"><div><h2>{all ? t('All messages') : t('Waiting')}</h2></div>
        <label className="remember-device"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /><span>{t('Show handled too')}</span></label></div>
      {list.isSuccess && !list.data.length ? <p className="muted">{t('No messages here.')}</p> : null}
      <div className="website-messages">
        {list.data?.map((m) => (
          <article key={m.id} className={`website-message ${m.handledAt ? 'is-handled' : ''}`}>
            <header><strong>{m.name}</strong>{m.company ? <span> · {m.company}</span> : null}<small>{new Date(m.createdAt).toLocaleString()}{m.locale ? ` · ${m.locale.toUpperCase()}` : ''}</small></header>
            <p>{m.message}</p>
            <footer>
              <a href={`mailto:${m.email}?subject=${encodeURIComponent('FakturaSmart Pro')}`}>{m.email}</a>{m.phone ? <span> · {m.phone}</span> : null}
              {m.handledAt ? <small>{t('Handled')} {new Date(m.handledAt).toLocaleDateString()}</small>
                : <button type="button" className="button button--small button--ghost" onClick={() => handled.mutate(m.id)}>{t('Mark as handled')}</button>}
            </footer>
          </article>
        ))}
      </div>
    </section>
  );
}

const PLAN_NAMES: Record<string, string> = { free: 'Free', basic: 'Basic', premium: 'Premium', full: 'Full accounting', firm: 'Firm' };

function planLabel(t: (s: string, v?: Record<string, string | number>) => string, c: AdminCompany): string {
  const s = c.subscription;
  if (!s) return '—';
  const state = s.state === 'free' ? t('free') : s.state === 'paid' ? t('paid until {date}', { date: s.paidUntil ?? '' })
    : s.state === 'trial' ? t('trial, {n} days left', { n: s.daysLeft ?? 0 }) : s.state === 'expired' ? t('expired') : '—';
  return `${PLAN_NAMES[s.plan]} · ${state}`;
}

/** Plan, trial end, paid-until (recorded by hand until payments exist) and the company's own price. */
function PlanDialog({ company, onClose, onSaved }: { company: AdminCompany | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { t } = useI18n();
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => { if (company) dialog.current?.showModal(); else dialog.current?.close(); }, [company]);
  const save = useMutation({ mutationFn: (body: unknown) => api.put(`api/admin/companies/${company!.id}/plan`, body), onSuccess: onSaved });
  const s = company?.subscription;
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const d = new FormData(event.currentTarget);
    const price = String(d.get('price') ?? '').trim().replace(',', '.');
    save.mutate({
      plan: d.get('plan'), trialEndsAt: String(d.get('trialEndsAt') || '') || null, paidUntil: String(d.get('paidUntil') || '') || null,
      priceCents: price === '' ? null : Math.round(Number(price) * 100), note: String(d.get('note') ?? ''),
    });
  };
  return (
    <dialog ref={dialog} className="confirm-dialog" onClose={onClose}>
      {company && s ? (
        <form className="dialog-card" onSubmit={submit} key={company.id}>
          <p className="eyebrow">{t('Plan')}</p><h2>{company.name}</h2>
          {save.isError ? <Alert kind="error">{errorText(t, save.error)}</Alert> : null}
          <label>{t('Plan')}<select name="plan" defaultValue={s.plan}>{Object.entries(PLAN_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
          <div className="form-grid">
            <label>{t('Trial ends')}<input type="date" name="trialEndsAt" defaultValue={s.trialEndsAt?.slice(0, 10) ?? ''} /></label>
            <label>{t('Paid until')}<input type="date" name="paidUntil" defaultValue={s.paidUntil ?? ''} /></label>
          </div>
          <label>{t('Monthly price, € before VAT (empty = list price, 0 = free)')}<input name="price" inputMode="decimal" defaultValue={s.priceCents === null ? '' : (s.priceCents / 100).toFixed(2)} placeholder={s.listPriceCents === null ? t('individual') : (s.listPriceCents / 100).toFixed(2)} /></label>
          <label>{t('Note')}<input name="note" defaultValue={company.planNote ?? ''} maxLength={500} /></label>
          <div className="dialog-actions">
            <button type="button" className="button button--ghost" onClick={onClose}>{t('Cancel')}</button>
            <button type="submit" className="button button--primary" disabled={save.isPending}>{t('Save')}</button>
          </div>
        </form>
      ) : null}
    </dialog>
  );
}

interface Coupon { id: string; code: string; active: boolean; used: number; maxRedemptions: number | null; created: string; percentOff: number | null; months: number }

/** Discount codes (Stripe promotion codes): make one, see how often it was used, switch it off. */
function AdminCoupons() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['admin-coupons'], queryFn: () => api.get<{ available: boolean; coupons: Coupon[] }>('api/admin/coupons') });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin-coupons'] });
  const create = useMutation({
    mutationFn: (form: HTMLFormElement) => {
      const d = new FormData(form);
      return api.post('api/admin/coupons', { code: String(d.get('code') ?? ''), percentOff: Number(d.get('percentOff')), months: Number(d.get('months')), maxRedemptions: d.get('maxRedemptions') ? Number(d.get('maxRedemptions')) : null })
        .then(() => form.reset());
    },
    onSuccess: refresh,
  });
  const off = useMutation({ mutationFn: (id: string) => api.post(`api/admin/coupons/${id}/off`), onSuccess: refresh });
  if (list.data && !list.data.available) return <section className="panel-card"><p className="muted">{t('Stripe is not set up on the server.')}</p></section>;
  return (
    <>
      <section className="panel-card">
        <h2>{t('New code')}</h2>
        {create.isError ? <Alert kind="error">{errorText(t, create.error)}</Alert> : null}
        <form className="form-grid form-grid--4" onSubmit={(e) => { e.preventDefault(); create.mutate(e.currentTarget); }}>
          <label>{t('Code')}<input name="code" required pattern="[A-Za-z0-9_-]{3,40}" placeholder="PIRMIEJI" /></label>
          <label>{t('Discount, %')}<input name="percentOff" type="number" min={1} max={100} required defaultValue={50} /></label>
          <label>{t('Months (0 = always)')}<input name="months" type="number" min={0} max={36} required defaultValue={6} /></label>
          <label>{t('How many companies (empty = no limit)')}<input name="maxRedemptions" type="number" min={1} /></label>
          <div className="form-actions"><button className="button button--primary" disabled={create.isPending}>{t('Create')}</button></div>
        </form>
      </section>
      <section className="panel-card">
        <div className="table-wrap"><table className="invoice-table">
          <thead><tr><th>{t('Code')}</th><th>{t('Discount')}</th><th>{t('Used')}</th><th>{t('Created')}</th><th /></tr></thead>
          <tbody>
            {list.data?.coupons.length === 0 ? <tr><td colSpan={5} className="empty">{t('No codes yet.')}</td></tr> : null}
            {list.data?.coupons.map((c) => (
              <tr key={c.id} className={c.active ? undefined : 'is-neutral'}>
                <td><strong>{c.code}</strong>{c.active ? null : <small>{t('Off')}</small>}</td>
                <td>−{c.percentOff}% · {c.months ? t('{n} months', { n: c.months }) : t('always')}</td>
                <td>{c.used}{c.maxRedemptions ? ` / ${c.maxRedemptions}` : ''}</td>
                <td>{c.created.slice(0, 10)}</td>
                <td className="right">{c.active ? <button type="button" className="text-danger" disabled={off.isPending} onClick={() => { if (confirm(t('Switch off {code}?', { code: c.code }))) off.mutate(c.id); }}>{t('Switch off')}</button> : null}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </section>
    </>
  );
}
